import { formatLegendValue } from "src/components/monitoring/chartUtils";
import { TimeFrame, TimeRange, Week } from "src/components/monitoring/commonTypes";
import { differenceInSeconds, eachWeekOfInterval, endOfMonth, endOfWeek, startOfMonth, startOfWeek } from "date-fns";
import {
    dateFormatterDaysAndMonths,
    dateFormatterHoursAndMinutes,
    dateFormatterYears,
} from "src/components/monitoring/senec-line-chart/lineChartUtils";
import dayjs from "dayjs";

/**
 * Generates `numberOfLabels` x-axis labels for line chart based on the given timeframe
 *
 * Label format:
 * < 1 day   -> hh:mm
 * > 1 day   -> if new year: yyyy
 *              if new day: dd:MM
 *              if same day, but different time: hh:mm
 */
export const getLineChartXAxisLabels = (
    timeFrame: TimeFrame,
    timeRange: TimeRange,
    locale: Intl.Locale,
    numberOfLabels = 12,
): string[] => {
    const dateValues: string[] = [];
    const DAY_IN_SECONDS = 86400;
    const startDate = dayjs(timeFrame.start).toDate();
    const endDate = dayjs(timeFrame.end).toDate();

    const durationInSeconds = differenceInSeconds(endDate, startDate);
    const intervalInSeconds = durationInSeconds / (numberOfLabels - 1);

    if (
        timeRange === TimeRange.ONE_HOUR ||
        timeRange === TimeRange.THREE_HOURS ||
        timeRange === TimeRange.DAY ||
        durationInSeconds <= DAY_IN_SECONDS
    ) {
        for (let i = 0; i < numberOfLabels; i++) {
            const legendValue: Date = new Date(startDate.getTime() + i * intervalInSeconds * 1000);
            dateValues.push(dateFormatterHoursAndMinutes(locale.toString()).format(legendValue));
        }
    } else {
        let prevLabelDate = new Date(startDate.getTime());

        for (let i = 0; i < numberOfLabels; i++) {
            const legendValue: Date = new Date(startDate.getTime() + i * intervalInSeconds * 1000);

            if (
                i !== 0 &&
                legendValue.getDay() === prevLabelDate.getDay() &&
                legendValue.getMonth() === prevLabelDate.getMonth() &&
                legendValue.getFullYear() === prevLabelDate.getFullYear()
            ) {
                dateValues.push(dateFormatterHoursAndMinutes(locale.toString()).format(legendValue));
            } else if (
                legendValue.getFullYear() !== prevLabelDate.getFullYear() ||
                (i === 0 && startDate.getFullYear() !== endDate.getFullYear())
            ) {
                prevLabelDate = legendValue;
                dateValues.push(dateFormatterYears(locale.toString()).format(legendValue));
            } else {
                prevLabelDate = legendValue;
                dateValues.push(dateFormatterDaysAndMonths(locale.toString()).format(legendValue));
            }
        }
    }

    return dateValues;
};

export function getWeeksOfMonth(dateOfMonth: Date): Week[] {
    const firstDayOfMonth = startOfMonth(dateOfMonth);
    const lastDayOfMonth = endOfMonth(dateOfMonth);

    const weekStarts = eachWeekOfInterval({ start: firstDayOfMonth, end: lastDayOfMonth }, { weekStartsOn: 1 });

    return weekStarts.map((weekStart, index) => {
        const from = index === 0 ? firstDayOfMonth : startOfWeek(weekStart, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
        const to = weekEnd > lastDayOfMonth ? lastDayOfMonth : weekEnd;
        return { from, to };
    });
}

/**
 *  Generates an array of labels for the Y-axis of a chart.
 *  @returns An array of five strings representing the Y-axis labels, starting from the highestNumber and decrementing
 *  down to "0".
 */
export function getLabelsForYAxis(highestNumber: number | undefined, locale: string, numberOfLines: number): string[] {
    if (highestNumber === undefined) {
        return [];
    } else if (highestNumber === 0) {
        return ["0"];
    }

    const decrementValue = highestNumber / (numberOfLines - 1);
    const result = [];
    for (let i = 0; i < numberOfLines - 1; i++) {
        const number = highestNumber - i * decrementValue;
        result.push(formatLegendValue(locale, number));
    }
    result.push("0");

    return result;
}
